import type { Database as Interface, Storage } from "../../design/services";
import type { Config, Entity, Model } from "../../design/types";
import { depopulate } from "./shared";

import type { FirebaseApp, FirebaseOptions } from "firebase/app";
import { getFirebaseApp } from "../shared/firebase";
import {
    getFirestore,
    Firestore,
    collection,
    doc,
    getDoc,
    getDocs,
    setDoc,
    deleteDoc,
    query,
    where,
    orderBy,
    onSnapshot,
    QueryConstraint,
    type QueryDocumentSnapshot
} from "firebase/firestore";

/** Active entity subscription for re-subscribing on condition change */
interface ActiveEntitySubscription {
    model: Model;
    id: string;
    populate: string[] | undefined;
    callback: (entity: Entity) => void;
    unsubscribe: () => void;
}

/** Active list subscription for re-subscribing on condition change */
interface ActiveListSubscription {
    model: Model;
    populate: string[] | undefined;
    filter: Record<string, unknown> | undefined;
    order: { by: string; direction: "asc" | "desc" } | undefined;
    options: { cursor?: string; limit?: number } | undefined;
    callback: (entities: Entity[]) => void;
    unsubscribe: () => void;
}

export class Database implements Interface {
    private firebaseApp: FirebaseApp;
    private db: Firestore;
    private storage: Storage;
    private projectId: string;
    private stagingStore: Map<string, Map<string, Entity>>;
    private stagingFileUrls: Map<string, Set<string>>;
    private activeEntitySubscriptions: Set<ActiveEntitySubscription>;
    private activeListSubscriptions: Set<ActiveListSubscription>;

    conditions: {
        read: Record<string, Record<string, unknown>>;
        write: Record<string, Record<string, unknown>>;
    } = { read: {}, write: {} };

    models: Model[] = [];

    constructor(storage: Storage, projectId: string, _config?: Config) {
        this.storage = storage;
        this.projectId = projectId;
        this.stagingStore = new Map();
        this.stagingFileUrls = new Map();
        this.activeEntitySubscriptions = new Set();
        this.activeListSubscriptions = new Set();

        const config = _config as unknown as FirebaseOptions;
        if (config) {
            this.firebaseApp = getFirebaseApp(config);
            this.db = getFirestore(this.firebaseApp);
        } else {
            throw new Error("Firebase config is required");
        }
    }
    ["constructor"](storage: Storage, projectId: string, config?: Config): Interface {
        return new Database(storage, projectId, config);
    }

    private storeKey(modelPath: string): string {
        return `${this.projectId}:${modelPath}`;
    }

    /** Firestore collection path: {projectId}/default{model.path}
     *  The placeholder doc keeps the path on an even segment count so
     *  entities land at valid doc references (col/doc/col/doc). */
    private collectionPath(modelPath: string): string {
        // model.path is e.g. "/users" → collection path "abc/default/users"
        const stripped = modelPath.startsWith('/') ? modelPath.slice(1) : modelPath;
        return `${this.projectId}/default/${stripped}`;
    }

    private stagingFileKey(storeKey: string, entityId: string): string {
        return `${storeKey}:${entityId}`;
    }

    setModels(models: Model[]): void {
        this.models = models;
    }

    private resolveModel(model: Model | string): Model {
        if (typeof model !== 'string') return model;
        const path = model.startsWith('/') ? model : '/' + model;
        const found = this.models.find(m => m.path === path);
        if (!found) throw new Error(`Model not found: '${model}'`);
        return found;
    }

    setConditions(conditions: { read: Record<string, Record<string, unknown>>; write: Record<string, Record<string, unknown>> }): void {
        this.conditions = conditions;
        // Re-subscribe all active list subscriptions with updated conditions.
        // Tear down the old Firestore listener and attach a new one, but update the
        // existing ActiveSubscription object in-place so callers' unsubscribe closures
        // (which captured that object) remain valid.
        for (const sub of this.activeListSubscriptions) {
            sub.unsubscribe();
            const collectionRef = collection(this.db, this.collectionPath(sub.model.path));
            const constraints = this.buildConstraints(sub.filter, sub.order, undefined, sub.model.name);
            const q = query(collectionRef, ...constraints);
            sub.unsubscribe = onSnapshot(q, async (snapshot) => {
                const records = await this.buildListResults(
                    sub.model, snapshot.docs, sub.populate, sub.filter, sub.order, sub.options
                );
                sub.callback(records);
            });
        }
        // Re-subscribe all active entity subscriptions with updated conditions.
        for (const sub of this.activeEntitySubscriptions) {
            sub.unsubscribe();
            const collectionRef = collection(this.db, this.collectionPath(sub.model.path));
            const docRef = doc(collectionRef, sub.id);
            sub.unsubscribe = onSnapshot(docRef, async (snapshot) => {
                const key = this.storeKey(sub.model.path);
                const stagingCollection = this.stagingStore.get(key);
                const stagingRecord = stagingCollection?.get(sub.id);
                if (stagingRecord) {
                    let result: Entity = structuredClone(stagingRecord);
                    if (sub.populate && sub.populate.length > 0) {
                        result = await this.populateRecord(result, sub.populate, sub.model);
                    }
                    sub.callback(result);
                    return;
                }
                if (!snapshot.exists()) return;
                let record: Entity = structuredClone({ ...snapshot.data(), id: snapshot.id }) as Entity;
                const readCondition = this.conditions.read[sub.model.name];
                if (readCondition && !this.matchesFilter(record as unknown as Record<string, unknown>, readCondition)) {
                    return;
                }
                if (sub.populate && sub.populate.length > 0) {
                    record = await this.populateRecord(record, sub.populate, sub.model);
                }
                sub.callback(record);
            });
        }
    }

    /**
     * Checks if a record matches a filter object.
     * Supports scalar equality, $in, and $contains operators.
     */
    private matchesFilter(record: Record<string, unknown>, filter?: Record<string, unknown>): boolean {
        if (!filter) return true;
        return Object.entries(filter).every(([key, value]) => {
            if (value && typeof value === 'object' && !Array.isArray(value)) {
                const op = value as Record<string, unknown>;
                if ('$in' in op) {
                    const inValues = op.$in;
                    if (!Array.isArray(inValues)) return false;
                    return inValues.includes(record[key]);
                }
                if ('$contains' in op) {
                    const fieldValue = record[key];
                    if (!Array.isArray(fieldValue)) return false;
                    return fieldValue.includes(op.$contains);
                }
            }
            return record[key] === value;
        });
    }

    async create(model: Model | string, data: Record<string, unknown>, id?: string, staging?: boolean): Promise<Entity> {
        model = this.resolveModel(model);
        data = depopulate(model, data);
        const key = this.storeKey(model.path);

        // Generate id: explicit arg > data.id > random UUID
        const recordId = id ?? (typeof data.id === 'string' ? data.id : crypto.randomUUID());

        if (staging) {
            // Staging create: check duplicates in staging only
            if (!this.stagingStore.has(key)) {
                this.stagingStore.set(key, new Map());
            }
            const stagingCollection = this.stagingStore.get(key)!;

            if (stagingCollection.has(recordId)) {
                throw new Error(`Record with id '${recordId}' already exists in model '${model.name}'`);
            }

            // Process file uploads (staging)
            const [processedData, fileUrls] = await this.processFiles(data, true);

            const record = {
                ...processedData,
                id: recordId,
                _createdAt: new Date().toISOString(),
                _updatedAt: new Date().toISOString()
            } as Entity;

            // Staging skips write conditions — they're enforced on commit()

            stagingCollection.set(recordId, record);

            // Track file URLs
            if (fileUrls.length > 0) {
                const fileKey = this.stagingFileKey(key, recordId);
                if (!this.stagingFileUrls.has(fileKey)) {
                    this.stagingFileUrls.set(fileKey, new Set());
                }
                for (const url of fileUrls) {
                    this.stagingFileUrls.get(fileKey)!.add(url);
                }
            }

            return record;
        }

        // Check staging first — if entity exists in staging, merge and commit
        const stagingCollection = this.stagingStore.get(key);
        const stagingRecord = stagingCollection?.get(recordId);
        if (stagingRecord) {
            const [processedData, fileUrls] = await this.processFiles(data, true);
            if (fileUrls.length > 0) {
                const fileKey = this.stagingFileKey(key, recordId);
                if (!this.stagingFileUrls.has(fileKey)) this.stagingFileUrls.set(fileKey, new Set());
                for (const url of fileUrls) this.stagingFileUrls.get(fileKey)!.add(url);
            }
            stagingCollection!.set(recordId, { ...stagingRecord, ...processedData, _updatedAt: new Date().toISOString() } as Entity);
            await this.commit(model, recordId);
            // Read back from Firestore
            const committedDoc = await getDoc(doc(collection(this.db, this.collectionPath(model.path)), recordId));
            return { ...committedDoc.data(), id: recordId } as Entity;
        }

        // Non-staging create: check duplicates in Firestore
        const collectionRef = collection(this.db, this.collectionPath(model.path));
        const docRef = doc(collectionRef, recordId);

        const existingDoc = await getDoc(docRef);
        if (existingDoc.exists()) {
            throw new Error(`Record with id '${recordId}' already exists in model '${model.name}'`);
        }

        // Process file uploads
        const [processedData] = await this.processFiles(data);

        const record = {
            ...processedData,
            id: recordId,
            _createdAt: new Date().toISOString(),
            _updatedAt: new Date().toISOString()
        } as Entity;

        // Check write condition before storing
        const writeCondition = this.conditions.write[model.name];
        if (writeCondition && !this.matchesFilter(record as unknown as Record<string, unknown>, writeCondition)) {
            throw new Error(`Write denied: record does not match condition for model '${model.name}'`);
        }

        await setDoc(docRef, record);

        // Notify list subscribers
        this.notifyListSubscribers(this.storeKey(model.path));

        return record;
    }

    get(model: Model | string, id: string, populate?: string[]): Promise<Entity>;
    get(model: Model | string, id: string, populate: string[] | undefined, subscribe: (entity: Entity) => void): () => void;
    get(model: Model | string, id: string, populate?: string[], subscribe?: (entity: Entity) => void): Promise<Entity> | (() => void) {
        model = this.resolveModel(model);
        if (subscribe) {
            return this.subscribeToEntity(model, id, populate, subscribe);
        }
        return this.fetchEntity(model, id, populate);
    }

    private subscribeToEntity(
        model: Model,
        id: string,
        populate: string[] | undefined,
        callback: (entity: Entity) => void
    ): () => void {
        const collectionRef = collection(this.db, this.collectionPath(model.path));
        const docRef = doc(collectionRef, id);

        const snapshotUnsub = onSnapshot(docRef, async (snapshot) => {
            // Check staging first
            const key = this.storeKey(model.path);
            const stagingCollection = this.stagingStore.get(key);
            const stagingRecord = stagingCollection?.get(id);

            if (stagingRecord) {
                // Staging skips read conditions — entity is being edited, always accessible
                let result: Entity = structuredClone(stagingRecord);
                if (populate && populate.length > 0) {
                    result = await this.populateRecord(result, populate, model);
                }
                callback(result);
                return;
            }

            if (!snapshot.exists()) return;

            let record: Entity = structuredClone({ ...snapshot.data(), id: snapshot.id }) as Entity;

            // Check read condition
            const readCondition = this.conditions.read[model.name];
            if (readCondition && !this.matchesFilter(record as unknown as Record<string, unknown>, readCondition)) {
                return;
            }

            if (populate && populate.length > 0) {
                record = await this.populateRecord(record, populate, model);
            }

            callback(record);
        });

        const activeSub: ActiveEntitySubscription = { model, id, populate, callback, unsubscribe: snapshotUnsub };
        this.activeEntitySubscriptions.add(activeSub);

        // Use activeSub.unsubscribe (property lookup) so setConditions can swap the
        // underlying listener without invalidating this closure.
        return () => {
            activeSub.unsubscribe();
            this.activeEntitySubscriptions.delete(activeSub);
        };
    }

    private async fetchEntity(model: Model, id: string, populate?: string[]): Promise<Entity> {
        const key = this.storeKey(model.path);

        // Check staging first, then Firestore
        const stagingCollection = this.stagingStore.get(key);
        const stagingRecord = stagingCollection?.get(id);

        if (stagingRecord) {
            // Staging skips read conditions — entity is being edited, always accessible
            let result: Entity = structuredClone(stagingRecord);
            if (populate && populate.length > 0) {
                result = await this.populateRecord(result, populate, model);
            }
            return result;
        }

        const collectionRef = collection(this.db, this.collectionPath(model.path));
        const docRef = doc(collectionRef, id);
        const docSnap = await getDoc(docRef);

        if (!docSnap.exists()) {
            throw new Error(`Record with id '${id}' not found in model '${model.name}'`);
        }

        // Clone to prevent callers from mutating shared state
        let result: Entity = structuredClone({ ...docSnap.data(), id: docSnap.id }) as Entity;

        // Check read condition — throw "not found" if filtered out (no information leakage)
        const readCondition = this.conditions.read[model.name];
        if (readCondition && !this.matchesFilter(result as unknown as Record<string, unknown>, readCondition)) {
            throw new Error(`Record with id '${id}' not found in model '${model.name}'`);
        }

        // Populate relationships if requested
        if (populate && populate.length > 0) {
            result = await this.populateRecord(result, populate, model);
        }

        return result;
    }

    list(
        model: Model | string,
        populate?: string[],
        filter?: Record<string, unknown>,
        order?: { by: string; direction: "asc" | "desc" },
        options?: { cursor?: string; limit?: number }
    ): Promise<Entity[]>;
    list(
        model: Model | string,
        populate: string[] | undefined,
        filter: Record<string, unknown> | undefined,
        order: { by: string; direction: "asc" | "desc" } | undefined,
        options: { cursor?: string; limit?: number } | undefined,
        subscribe: (entities: Entity[]) => void
    ): () => void;
    list(
        model: Model | string,
        populate?: string[],
        filter?: Record<string, unknown>,
        order?: { by: string; direction: "asc" | "desc" },
        options?: { cursor?: string; limit?: number },
        subscribe?: (entities: Entity[]) => void
    ): Promise<Entity[]> | (() => void) {
        model = this.resolveModel(model);
        if (subscribe) {
            return this.subscribeToList(model, populate, filter, order, options, subscribe);
        }
        return this.fetchList(model, populate, filter, order, options);
    }

    private subscribeToList(
        model: Model,
        populate: string[] | undefined,
        filter: Record<string, unknown> | undefined,
        order: { by: string; direction: "asc" | "desc" } | undefined,
        options: { cursor?: string; limit?: number } | undefined,
        callback: (entities: Entity[]) => void
    ): () => void {
        const collectionRef = collection(this.db, this.collectionPath(model.path));
        const constraints = this.buildConstraints(filter, order, options, model.name);
        const q = query(collectionRef, ...constraints);

        const snapshotUnsub = onSnapshot(q, async (snapshot) => {
            const records = await this.buildListResults(model, snapshot.docs, populate, filter, order, options);
            callback(records);
        });

        const activeSub: ActiveListSubscription = { model, populate, filter, order, options, callback, unsubscribe: snapshotUnsub };
        this.activeListSubscriptions.add(activeSub);

        // Use activeSub.unsubscribe (property lookup) so setConditions can swap the
        // underlying listener without invalidating this closure.
        return () => {
            activeSub.unsubscribe();
            this.activeListSubscriptions.delete(activeSub);
        };
    }

    /**
     * Merge Firestore docs with staging, apply mandatory-read + call-site
     * filter, in-memory sort, cursor, limit, clone, and population. Shared
     * between fetchList, subscribeToList, and setConditions' relisten loop —
     * staging, cursors, and limits all need to see the post-merge set, so
     * the pipeline can't be pushed to Firestore.
     */
    private async buildListResults(
        model: Model,
        firestoreDocs: QueryDocumentSnapshot[],
        populate: string[] | undefined,
        filter: Record<string, unknown> | undefined,
        order: { by: string; direction: "asc" | "desc" } | undefined,
        options: { cursor?: string; limit?: number } | undefined,
    ): Promise<Entity[]> {
        const merged = new Map<string, Entity>();
        for (const d of firestoreDocs) {
            merged.set(d.id, { ...d.data(), id: d.id } as Entity);
        }
        const key = this.storeKey(model.path);
        const stagingCollection = this.stagingStore.get(key);
        if (stagingCollection) {
            for (const [id, entity] of stagingCollection) merged.set(id, entity);
        }

        let records: Entity[] = Array.from(merged.values());

        const readCondition = this.conditions.read[model.name];
        const mergedFilter = readCondition ? { ...filter, ...readCondition } : filter;
        if (mergedFilter) {
            records = records.filter(record =>
                this.matchesFilter(record as unknown as Record<string, unknown>, mergedFilter)
            );
        }

        if (order) {
            const o = order;
            records.sort((a, b) => {
                const aVal = (a as unknown as Record<string, unknown>)[o.by];
                const bVal = (b as unknown as Record<string, unknown>)[o.by];
                if (aVal == null && bVal == null) return 0;
                if (aVal == null) return o.direction === "asc" ? -1 : 1;
                if (bVal == null) return o.direction === "asc" ? 1 : -1;
                if (typeof aVal === "string" && typeof bVal === "string") {
                    return o.direction === "asc" ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
                }
                if (typeof aVal === "number" && typeof bVal === "number") {
                    return o.direction === "asc" ? aVal - bVal : bVal - aVal;
                }
                const aStr = String(aVal);
                const bStr = String(bVal);
                return o.direction === "asc" ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
            });
        }

        if (options?.cursor) {
            const cursorIndex = records.findIndex(r => r.id === options.cursor);
            if (cursorIndex !== -1) records = records.slice(cursorIndex + 1);
        }
        if (options?.limit !== undefined) records = records.slice(0, options.limit);

        records = records.map(record => structuredClone(record));

        if (populate && populate.length > 0) {
            records = await Promise.all(
                records.map(record => this.populateRecord(record, populate, model))
            );
        }
        return records;
    }

    private buildConstraints(
        filter?: Record<string, unknown>,
        order?: { by: string; direction: "asc" | "desc" },
        _options?: { cursor?: string; limit?: number },
        modelName?: string
    ): QueryConstraint[] {
        // Merge mandatory read condition with call-site filter (mandatory wins)
        const readCondition = modelName ? this.conditions.read[modelName] : undefined;
        filter = readCondition ? { ...filter, ...readCondition } : filter;
        const constraints: QueryConstraint[] = [];

        if (filter) {
            for (const [key, value] of Object.entries(filter)) {
                if (value === undefined) continue;
                if (value && typeof value === 'object' && !Array.isArray(value)) {
                    const op = value as Record<string, unknown>;
                    if ('$in' in op && Array.isArray(op.$in)) {
                        // Firestore limits 'in' to 30 values; skip constraint and filter in-memory if exceeded
                        // TODO: batch $in into chunks of 30, run multiple queries, and merge results
                        if (op.$in.length <= 30) constraints.push(where(key, "in", op.$in));
                    } else if ('$contains' in op) {
                        constraints.push(where(key, "array-contains", op.$contains));
                    }
                } else {
                    constraints.push(where(key, "==", value));
                }
            }
        }

        if (order) {
            constraints.push(orderBy(order.by, order.direction));
        }

        // Limit and cursor are applied in-memory after staging merge,
        // so they are NOT pushed as Firestore constraints.

        return constraints;
    }

    private async fetchList(
        model: Model,
        populate?: string[],
        filter?: Record<string, unknown>,
        order?: { by: string; direction: "asc" | "desc" },
        options?: { cursor?: string; limit?: number }
    ): Promise<Entity[]> {
        const collectionRef = collection(this.db, this.collectionPath(model.path));
        // Don't push limit to Firestore — staging merge can change the result set,
        // so limit is applied in-memory after merge/filter/sort/cursor.
        const constraints = this.buildConstraints(filter, order, undefined, model.name);

        const q = query(collectionRef, ...constraints);
        const querySnapshot = await getDocs(q);
        return this.buildListResults(model, querySnapshot.docs, populate, filter, order, options);
    }

    async update(model: Model | string, id: string, data: Record<string, unknown>, staging?: boolean): Promise<Entity> {
        model = this.resolveModel(model);
        data = depopulate(model, data);
        const key = this.storeKey(model.path);

        if (staging) {
            // Staging update: only updates entities in stagingStore
            const stagingCollection = this.stagingStore.get(key);
            if (!stagingCollection) {
                throw new Error(`Record with id '${id}' not found in model '${model.name}'`);
            }

            const record = stagingCollection.get(id);
            if (!record) {
                throw new Error(`Record with id '${id}' not found in model '${model.name}'`);
            }

            // Staging skips write conditions — they're enforced on commit()

            // Process file uploads (staging)
            const [processedData, fileUrls] = await this.processFiles(data, true);

            const updatedRecord = {
                ...record,
                ...processedData,
                _updatedAt: new Date().toISOString()
            } as Entity;

            stagingCollection.set(id, updatedRecord);

            // Track new file URLs (accumulate)
            if (fileUrls.length > 0) {
                const fileKey = this.stagingFileKey(key, id);
                if (!this.stagingFileUrls.has(fileKey)) {
                    this.stagingFileUrls.set(fileKey, new Set());
                }
                for (const url of fileUrls) {
                    this.stagingFileUrls.get(fileKey)!.add(url);
                }
            }

            return updatedRecord;
        }

        // Check staging first — staging is the working copy and takes priority
        const stagingCollection = this.stagingStore.get(key);
        const stagingRecord = stagingCollection?.get(id);
        if (stagingRecord) {
            const [processedData, fileUrls] = await this.processFiles(data, true);
            if (fileUrls.length > 0) {
                const fileKey = this.stagingFileKey(key, id);
                if (!this.stagingFileUrls.has(fileKey)) this.stagingFileUrls.set(fileKey, new Set());
                for (const url of fileUrls) this.stagingFileUrls.get(fileKey)!.add(url);
            }
            stagingCollection!.set(id, { ...stagingRecord, ...processedData, _updatedAt: new Date().toISOString() } as Entity);
            await this.commit(model, id);
            const committedDoc = await getDoc(doc(collection(this.db, this.collectionPath(model.path)), id));
            return { ...committedDoc.data(), id } as Entity;
        }

        const collectionRef = collection(this.db, this.collectionPath(model.path));
        const docRef = doc(collectionRef, id);

        const docSnap = await getDoc(docRef);
        if (!docSnap.exists()) {
            throw new Error(`Record with id '${id}' not found in model '${model.name}'`);
        }

        const existingRecord = docSnap.data();

        // Check write condition — throw "not found" if filtered out
        const writeCondition = this.conditions.write[model.name];
        if (writeCondition && !this.matchesFilter(existingRecord as Record<string, unknown>, writeCondition)) {
            throw new Error(`Record with id '${id}' not found in model '${model.name}'`);
        }

        // Process file uploads
        const [processedData] = await this.processFiles(data);

        const updatedRecord = {
            ...existingRecord,
            ...processedData,
            id,
            _updatedAt: new Date().toISOString()
        } as Entity;

        await setDoc(docRef, updatedRecord);

        // Notify subscribers
        this.notifyEntitySubscribers(`${this.storeKey(model.path)}:${id}`);
        this.notifyListSubscribers(this.storeKey(model.path));

        return updatedRecord;
    }

    async delete(model: Model | string, id: string, staging?: boolean): Promise<void> {
        model = this.resolveModel(model);
        const key = this.storeKey(model.path);
        let found = false;

        // Delete from staging store if present
        const stagingCollection = this.stagingStore.get(key);
        if (stagingCollection) {
            const record = stagingCollection.get(id);
            if (record) {
                // Check write condition
                const writeCondition = this.conditions.write[model.name];
                if (writeCondition && !this.matchesFilter(record as unknown as Record<string, unknown>, writeCondition)) {
                    throw new Error(`Record with id '${id}' not found in model '${model.name}'`);
                }

                stagingCollection.delete(id);

                // Discard tracked file URLs
                const fileKey = this.stagingFileKey(key, id);
                const urls = this.stagingFileUrls.get(fileKey);
                if (urls) {
                    await this.storage.discard([...urls]);
                    this.stagingFileUrls.delete(fileKey);
                }

                found = true;
            }
        }

        // Delete from Firestore if present (skip when staging-only)
        if (!staging) {
            const collectionRef = collection(this.db, this.collectionPath(model.path));
            const docRef = doc(collectionRef, id);

            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                // Check write condition — throw "not found" if filtered out
                const writeCondition = this.conditions.write[model.name];
                if (writeCondition && !this.matchesFilter(docSnap.data() as Record<string, unknown>, writeCondition)) {
                    throw new Error(`Record with id '${id}' not found in model '${model.name}'`);
                }

                await deleteDoc(docRef);
                found = true;
            }
        }

        if (!found) {
            throw new Error(`Record with id '${id}' not found in model '${model.name}'`);
        }

        // Remove entity subscriptions for deleted entity
        for (const sub of this.activeEntitySubscriptions) {
            if (sub.model.path === model.path && sub.id === id) {
                sub.unsubscribe();
                this.activeEntitySubscriptions.delete(sub);
            }
        }

        // Notify list subscribers (only when real store was modified)
        if (!staging) {
            this.notifyListSubscribers(key);
        }
    }

    async commit(model: Model | string, id: string): Promise<void> {
        model = this.resolveModel(model);
        const key = this.storeKey(model.path);

        // Get entity from staging
        const stagingCollection = this.stagingStore.get(key);
        if (!stagingCollection) {
            throw new Error(`Record with id '${id}' not found in staging for model '${model.name}'`);
        }

        const stagingEntity = stagingCollection.get(id);
        if (!stagingEntity) {
            throw new Error(`Record with id '${id}' not found in staging for model '${model.name}'`);
        }

        // Enforce write conditions on commit
        const writeCondition = this.conditions.write[model.name];
        if (writeCondition && !this.matchesFilter(stagingEntity as unknown as Record<string, unknown>, writeCondition)) {
            throw new Error(`Write denied: record does not match condition for model '${model.name}'`);
        }

        // Commit staged files and rewrite URLs
        const fileKey = this.stagingFileKey(key, id);
        const urls = this.stagingFileUrls.get(fileKey);
        if (urls) {
            const urlMapping = await this.storage.commit([...urls]);
            this.stagingFileUrls.delete(fileKey);
            const rewritten = this.rewriteUrls(stagingEntity, urlMapping);
            Object.assign(stagingEntity, rewritten);
        }

        // Write to Firestore (create-or-update)
        const collectionRef = collection(this.db, this.collectionPath(model.path));
        const docRef = doc(collectionRef, id);

        const existingDoc = await getDoc(docRef);
        if (existingDoc.exists()) {
            // Update: merge staged data over existing
            const merged = {
                ...existingDoc.data(),
                ...stagingEntity,
            };
            await setDoc(docRef, merged);
        } else {
            // Create new entry
            await setDoc(docRef, stagingEntity);
        }

        // Clean up staging
        stagingCollection.delete(id);

        // Notify subscribers
        this.notifyEntitySubscribers(`${key}:${id}`);
        this.notifyListSubscribers(key);
    }

    /**
     * Notifies entity subscribers when a specific entity changes.
     */
    private async notifyEntitySubscribers(key: string): Promise<void> {
        // key format: "projectId:modelPath:entityId"
        const lastColon = key.lastIndexOf(':');
        const entityId = key.substring(lastColon + 1);

        for (const sub of this.activeEntitySubscriptions) {
            const subKey = `${this.storeKey(sub.model.path)}:${sub.id}`;
            if (subKey === key) {
                try {
                    const entity = await this.fetchEntity(sub.model, entityId, sub.populate);
                    sub.callback(entity);
                } catch (err) {
                    if (err instanceof Error && err.message.includes('not found')) continue;
                    console.error(`notifyEntitySubscribers error [model=${sub.model.name}, id=${entityId}, populate=${sub.populate?.join(',') ?? 'none'}]:`, err);
                }
            }
        }
    }

    /**
     * Notifies list subscribers when any entity in a model changes.
     */
    private async notifyListSubscribers(modelPath: string): Promise<void> {
        for (const sub of this.activeListSubscriptions) {
            const subKey = this.storeKey(sub.model.path);
            if (subKey === modelPath) {
                const records = await this.fetchList(
                    sub.model,
                    sub.populate,
                    sub.filter,
                    sub.order,
                    sub.options
                );
                sub.callback(records);
            }
        }
    }

    /**
     * Processes data to upload File objects and replace them with URLs.
     * Returns [processedData, fileUrls] tuple.
     */
    private async processFiles(data: Record<string, unknown>, staging?: boolean): Promise<[Record<string, unknown>, string[]]> {
        const fileUrls: string[] = [];
        const visited = new WeakSet();

        const processValue = async (value: unknown): Promise<unknown> => {
            // FileMetadata with pending file upload (created by Image/Pdf components)
            if (value !== null && typeof value === 'object' && !(value instanceof File) && 'file' in value && (value as Record<string, unknown>).file instanceof File) {
                const meta = value as Record<string, unknown>;
                const file = meta.file as File;
                const url = await this.storage.upload(file, staging);
                fileUrls.push(url);
                const { file: _, ...rest } = meta;
                return { ...rest, url };
            }
            if (Array.isArray(value)) {
                if (visited.has(value)) { console.error('processFiles: circular array reference detected, skipping'); return value; }
                visited.add(value);
                return Promise.all(value.map(item => processValue(item)));
            }
            if (value !== null && typeof value === 'object') {
                if (visited.has(value as object)) { console.error('processFiles: circular object reference detected, skipping'); return value; }
                visited.add(value as object);
                const result: Record<string, unknown> = {};
                for (const [k, v] of Object.entries(value)) {
                    result[k] = await processValue(v);
                }
                return result;
            }
            return value;
        };

        const processed = await processValue(data) as Record<string, unknown>;

        return [processed, fileUrls];
    }

    /**
     * Recursively replaces URL strings in an object/array using a mapping.
     */
    private rewriteUrls(obj: unknown, mapping: Record<string, string>, visited = new WeakSet()): unknown {
        if (typeof obj === 'string') return obj in mapping ? mapping[obj] : obj;
        if (Array.isArray(obj)) {
            if (visited.has(obj)) return obj;
            visited.add(obj);
            return obj.map(item => this.rewriteUrls(item, mapping, visited));
        }
        if (obj !== null && typeof obj === 'object') {
            if (visited.has(obj as object)) return obj;
            visited.add(obj as object);
            const result: Record<string, unknown> = {};
            for (const [k, v] of Object.entries(obj)) {
                result[k] = this.rewriteUrls(v, mapping, visited);
            }
            return result;
        }
        return obj;
    }

    /**
     * Gets all populatable properties (reference or object type) from a model
     */
    private getPopulatableProperties(model: Model) {
        return model.properties.filter(p =>
            (p.type === 'reference' && p.reference) ||
            (p.type === 'object' && (p.reference || p.properties))
        );
    }

    /**
     * Expands glob patterns to concrete field instructions
     * Supports both '/' and '.' as separators (e.g., 'author/posts' or 'author.posts')
     * Returns array of { field, nestedPattern } objects
     */
    private expandPattern(pattern: string, model: Model): Array<{ field: string; nestedPattern: string | null }> {
        // Normalize: convert dots to slashes for uniform processing
        const normalized = pattern.replace(/\./g, '/');
        const parts = normalized.split('/');
        const first = parts[0];
        const rest = parts.slice(1).join('/') || null;

        if (first === '**') {
            const props = this.getPopulatableProperties(model);
            return props.map(p => ({ field: p.id, nestedPattern: rest ? `**/${rest}` : '**/*' }));
        }

        if (first === '*') {
            const props = this.getPopulatableProperties(model);
            return props.map(p => ({ field: p.id, nestedPattern: rest }));
        }

        return [{ field: first, nestedPattern: rest }];
    }

    /**
     * Looks up a related record by checking staging first, then Firestore.
     */
    private async lookupRelatedRecord(refModel: Model, id: string): Promise<Entity | null> {
        const key = this.storeKey(refModel.path);

        // Check staging first
        const stagingCollection = this.stagingStore.get(key);
        const stagingRecord = stagingCollection?.get(id);
        if (stagingRecord) return stagingRecord;

        // Fall back to Firestore
        const collectionRef = collection(this.db, this.collectionPath(refModel.path));
        const docRef = doc(collectionRef, id);
        const snap = await getDoc(docRef);
        if (snap.exists()) {
            return { ...snap.data(), id: snap.id } as Entity;
        }
        return null;
    }

    /**
     * Populates relationship fields in a record
     * Supports glob-style patterns: *, *\\/*, **\\/*
     */
    private async populateRecord(
        record: Entity,
        populate: string[],
        model: Model,
        cache: Map<string, Entity> = new Map()
    ): Promise<Entity> {
        const populated: Entity = { ...record };

        // Cache doubles as visited set — prevents circular references and reuses resolved entities
        const recordKey = `${model.path}:${record.id}`;
        if (cache.has(recordKey)) {
            return cache.get(recordKey)!;
        }
        cache.set(recordKey, populated);

        // Expand all patterns and group by field
        const fieldInstructions = new Map<string, string[]>();

        for (const pattern of populate) {
            const expanded = this.expandPattern(pattern, model);
            for (const { field, nestedPattern } of expanded) {
                if (!fieldInstructions.has(field)) {
                    fieldInstructions.set(field, []);
                }
                if (nestedPattern) {
                    fieldInstructions.get(field)!.push(nestedPattern);
                }
            }
        }

        // Populate each field
        for (const [fieldName, nestedPatterns] of fieldInstructions) {
            // Match on id (field key) or name (for backwards compatibility)
            const property = model.properties.find(p => p.id === fieldName || p.name === fieldName);
            if (!property) continue;

            // Records use property.name as keys
            const value = (record as unknown as Record<string, unknown>)[property.name];
            if (value === undefined || value === null) continue;

            if ((property.type === 'reference' || (property.type === 'object' && property.reference)) && property.reference) {
                // Read condition for the referenced model
                const refReadCondition = this.conditions.read[property.reference.name];

                if (Array.isArray(value)) {
                    // Array of references
                    const populatedArray = await Promise.all(
                        value.map(async (refId: string | Entity) => {
                            // Handle already-populated references (when re-populating)
                            const id = typeof refId === 'object' && refId !== null && 'id' in refId
                                ? refId.id
                                : refId;

                            const relatedRecord = await this.lookupRelatedRecord(property.reference!, id as string);
                            if (!relatedRecord) return null;

                            // Check read condition on referenced model
                            if (refReadCondition && !this.matchesFilter(relatedRecord as unknown as Record<string, unknown>, refReadCondition)) {
                                return null;
                            }

                            if (nestedPatterns.length > 0) {
                                return await this.populateRecord(
                                    relatedRecord, nestedPatterns, property.reference!, cache
                                );
                            }
                            return relatedRecord;
                        })
                    );
                    (populated as unknown as Record<string, unknown>)[property.name] = populatedArray.filter(r => r !== null);
                } else {
                    // Single reference - handle already-populated references
                    const id = typeof value === 'object' && value !== null && 'id' in value
                        ? (value as Entity).id
                        : value as string;

                    const relatedRecord = await this.lookupRelatedRecord(property.reference!, id);
                    if (relatedRecord) {
                        if (refReadCondition && !this.matchesFilter(relatedRecord as unknown as Record<string, unknown>, refReadCondition)) {
                            (populated as unknown as Record<string, unknown>)[property.name] = null;
                        } else if (nestedPatterns.length > 0) {
                            (populated as unknown as Record<string, unknown>)[property.name] = await this.populateRecord(
                                relatedRecord, nestedPatterns, property.reference!, cache
                            );
                        } else {
                            (populated as unknown as Record<string, unknown>)[property.name] = relatedRecord;
                        }
                    } else {
                        console.error(`Referenced ${property.reference!.name} with id '${id}' not found`);
                        (populated as unknown as Record<string, unknown>)[property.name] = null;
                    }
                }
            } else if (property.type === 'object' && property.properties) {
                // Object type with nested properties
                if (nestedPatterns.length > 0 && typeof value === 'object') {
                    const nestedModel: Model = {
                        id: `${model.id}_${recordKey}`,
                        name: `${model.name}_${recordKey}`,
                        type: 'singleton' as const,
                        path: '',
                        properties: property.properties,
                        actions: []
                    };

                    if (Array.isArray(value)) {
                        const populatedArray = await Promise.all(
                            value.map(async (item: Entity) => {
                                if (item && typeof item === 'object' && 'id' in item) {
                                    return await this.populateRecord(item, nestedPatterns, nestedModel, cache);
                                }
                                return item;
                            })
                        );
                        (populated as unknown as Record<string, unknown>)[property.name] = populatedArray;
                    } else if ('id' in (value as object)) {
                        (populated as unknown as Record<string, unknown>)[property.name] = await this.populateRecord(
                            value as Entity, nestedPatterns, nestedModel, cache
                        );
                    }
                }
            }
        }

        return populated;
    }
}
